import { useMemo } from 'react';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils';
import useAppStore from '../../../store/rootStore.ts';
import { parseTierLadderUsd } from '../components/partner/partner-tier-math.ts';
import { useProfileReferrals } from './useProfileReferrals.ts';

/**
 * Partner check for always-mounted callers (e.g. the nav). `select` narrows the observed value to a
 * boolean so a poll can't re-render the nav, and the tier only moves at an epoch snapshot, so this
 * observer doesn't poll — the referrals page's own observers keep the cache fresh.
 */
export const useIsPartner = (): boolean =>
  useProfileReferrals({
    select: (data) => (data.partner_tier ?? 0) > 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  }).data ?? false;

/**
 * Partner-program bundle for the /referrals dashboard, with amounts already scaled to USD.
 * Everything here is partner-only API data; partners are onboarded manually, so
 * `rewards.partner_min_volume` is intentionally unused.
 */
export const usePartnerProgram = () => {
  const { data: referrals } = useProfileReferrals();
  const collateralDecimals = useAppStore((state) => state.exchange?.token?.decimals ?? 6);

  return useMemo(() => {
    const partnerTier = referrals?.partner_tier ?? 0;
    const partnerProgram = referrals?.partner_program;

    return {
      partnerProgram,
      partnerTier,
      // Tiers are 1-based: tier N is the Nth entry.
      tierConfig: partnerTier > 0 ? partnerProgram?.tiers[partnerTier - 1] : undefined,
      ladderUsd: parseTierLadderUsd(partnerProgram?.tiers, collateralDecimals),
      liveVolumeUsd: offsetNumberToNumber(Number(referrals?.cur_referred_volume ?? 0), collateralDecimals),
      nextPartnerTier: referrals?.next_partner_tier,
      alias: referrals?.alias,
    };
  }, [referrals, collateralDecimals]);
};
